// useNameAuth — learner-side identity flow (Day 12 rewrite).
//
// Flow:
//   1. Read goodbye:userId from localStorage.
//   2. If present:
//      - signInAnonymously (gets a Firebase anon uid, needed for Firestore Rules)
//      - validate the stored userId still exists in /users (cheap doc read)
//      - status = "signed-in" with that userId
//   3. If absent: status = "needs-name" → routes redirect to /name-login.
//
// signInAnonymously is idempotent in firebase/auth — calling when already
// signed in returns the existing user.

import { getFirebase, signInAnon, userDoc } from "@goodbye/firebase";
import { getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useEffect, useState } from "react";

const STORAGE_KEY = "goodbye:userId";
const COHORT_KEY = "goodbye:cohortId";
const WELCOMED_KEY = "goodbye:welcomed";

export type NameAuthStatus =
  | "loading"
  | "signed-in"
  | "needs-name"
  | "user-missing" // localStorage had a userId but /users/<id> doesn't exist any more
  | "error";

export interface NameAuthState {
  status: NameAuthStatus;
  /** docId of the learner in /users — null until signed-in */
  userId: string | null;
  /** role from the /users doc ("learner" | "worker"). 2026-05-30: workers
   *  (u15-u23) get role:"worker" so the learner app can reveal the test map
   *  to them. Preview mode (?preview=true) is treated as worker. */
  role: string | null;
  error: Error | null;
}

const INITIAL: NameAuthState = {
  status: "loading",
  userId: null,
  role: null,
  error: null,
};

export function useNameAuth(): NameAuthState {
  const [state, setState] = useState<NameAuthState>(INITIAL);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const stored =
        typeof window === "undefined"
          ? null
          : window.localStorage.getItem(STORAGE_KEY);

      // ?preview=true (sticky via sessionStorage): workers QA'ing learner UI
      // without name input. Sign in anonymously so Firestore Rules pass,
      // but report status as "signed-in" with sentinel userId.
      const isPreview =
        typeof window !== "undefined" &&
        (new URLSearchParams(window.location.search).get("preview") === "true" ||
          window.sessionStorage.getItem("goodbye:preview") === "true");
      if (
        typeof window !== "undefined" &&
        new URLSearchParams(window.location.search).get("preview") === "true"
      ) {
        try {
          window.sessionStorage.setItem("goodbye:preview", "true");
        } catch {
          /* sessionStorage may be unavailable */
        }
      }

      if (!stored && isPreview) {
        try {
          await signInAnon();
          await new Promise<void>((resolve) => {
            const unsub = onAuthStateChanged(getFirebase().auth, (u) => {
              if (u) {
                unsub();
                resolve();
              }
            });
          });
          if (cancelled) return;
          // Use u01 as sentinel so SyncProvider can subscribe to a real
          // learner's markers/pins (handy for visual QA of map etc.).
          // Preview = worker QA → role:"worker" so the test map shows.
          setState({ status: "signed-in", userId: "u01", role: "worker", error: null });
        } catch (err) {
          if (cancelled) return;
          setState({
            status: "error",
            userId: null,
            role: null,
            error: err instanceof Error ? err : new Error(String(err)),
          });
        }
        return;
      }

      if (!stored) {
        if (!cancelled)
          setState({ status: "needs-name", userId: null, role: null, error: null });
        return;
      }

      try {
        await signInAnon();
        // Wait one auth-state tick so Firestore picks up the credential
        // before we query.
        await new Promise<void>((resolve) => {
          const unsub = onAuthStateChanged(getFirebase().auth, (u) => {
            if (u) {
              unsub();
              resolve();
            }
          });
        });

        const snap = await getDoc(userDoc(stored));
        if (cancelled) return;
        if (!snap.exists()) {
          window.localStorage.removeItem(STORAGE_KEY);
          setState({ status: "user-missing", userId: null, role: null, error: null });
          return;
        }
        const data = snap.data() as { role?: string; cohortId?: string };
        const role = data.role ?? "learner";
        const cohortId = data.cohortId ?? "2025";
        window.localStorage.setItem(COHORT_KEY, cohortId);
        setState({ status: "signed-in", userId: stored, role, error: null });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          userId: null,
          role: null,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/** Persist learner identity after a successful name lookup. */
export function setLearnerIdentity(userId: string): void {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, userId);
  }
}

/** Persist cohort identity alongside learner identity. */
export function setCohortId(cohortId: string): void {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(COHORT_KEY, cohortId);
  }
}

/** Clear learner identity (logout / re-login). */
export function clearLearnerIdentity(): void {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(STORAGE_KEY);
  }
}

/** Clear identity + hard-redirect to root so AuthGate re-evaluates. */
export function logoutLearner(): void {
  clearLearnerIdentity();
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(WELCOMED_KEY); // also clear welcome flag
    window.location.replace("/");
  }
}

/**
 * Day 13 PR-C (D10): mark this PWA install as "has seen the Welcome page
 * once". Future logins skip the ceremonial Welcome and go straight to
 * Playlist. Because iOS PWA isolates localStorage from Safari tab, a
 * fresh PWA install always sees Welcome at least once.
 */
export function markWelcomed(): void {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(WELCOMED_KEY, "true");
  }
}

export function hasWelcomed(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(WELCOMED_KEY) === "true";
}

/**
 * Normalize a name for matching: strip all whitespace (incl. 全形) and
 * lowercase. Lets learners type their name with or without spaces, with
 * any casing — e.g. "vasil" / "Vasil" / "VASIL" all match the canonical
 * "Vasil" stored in /users/{uid}.name.
 */
export function normalizeName(s: string): string {
  return s.replace(/[\s　]+/g, "").toLowerCase();
}
